import * as React from 'react';
import { X } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Label } from '../Label';
import { comboboxChipVariants } from '../Combobox/variants';
import useI18n from '../../hooks/useI18n';
import type { ChipInputProps } from './types';
import { EditableChip } from './EditableChip';
import { useChipInputState } from './useChipInputState';

export const ChipInput = React.forwardRef<HTMLInputElement, ChipInputProps>(
  (
    {
      value,
      defaultValue = [],
      onValueChange,
      label,
      error = false,
      editable = false,
      validateEdit,
      addOnBlur = true,
      separatorPattern,
      deduplicate = true,
      placeholder,
      chipClassName,
      chipLabelClassName,
      chipsContainerClassName,
      inputClassName,
      containerClassName,
      disabled,
      id,
      required,
      onBlur,
      onKeyDown,
      ...inputProps
    },
    ref,
  ) => {
    const { t } = useI18n();
    const {
      chips,
      inputValue,
      setInputValue,
      announcement,
      addChips,
      editChip,
      removeChip,
    } = useChipInputState({
      value,
      defaultValue,
      onValueChange,
      deduplicate,
      separatorPattern,
      validateEdit,
    });
    const fieldRef = React.useRef<HTMLDivElement>(null);
    const generatedId = React.useId();
    const inputId = id ?? generatedId;
    const hasLabel = label != null;
    const inputRef = React.useRef<HTMLInputElement>(null);
    const chipRefs = React.useRef<(HTMLButtonElement | null)[]>([]);
    const pendingFocusRef = React.useRef<number | 'input' | null>(null);

    React.useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

    // Wait for the removed chip to leave the DOM before moving focus.
    React.useLayoutEffect(() => {
      const target = pendingFocusRef.current;
      if (target === null) return;

      pendingFocusRef.current = null;
      const chip = target === 'input' ? null : chipRefs.current[target];
      (chip ?? inputRef.current)?.focus();
    }, [chips]);
    const focusChip = (index: number) => {
      requestAnimationFrame(() => {
        const entries = fieldRef.current?.querySelectorAll(
          '[data-slot="editable-chip"]',
        );
        const target =
          entries?.[
            Math.min(index, entries.length - 1)
          ]?.querySelector<HTMLElement>('[data-slot="chip-edit"], input') ??
          fieldRef.current?.querySelector<HTMLInputElement>(
            '[data-slot="chip-input-control"]',
          );
        target?.focus();
      });
    };

    const handleRemoveChip = (id: number, index: number) => {
      if (removeChip(id)) focusChip(index);
    };

    const removeChipAndFocus = (
      index: number,
      focusTarget: number | 'input',
    ) => {
      if (removeChip(chips[index].id)) {
        pendingFocusRef.current = focusTarget;
      }
    };

    const handleChipKeyDown = (
      event: React.KeyboardEvent<HTMLButtonElement>,
      index: number,
    ) => {
      if (disabled || inputProps.readOnly) return;
      if (event.key !== 'Backspace' && event.key !== 'Delete') return;

      event.preventDefault();
      // Focus the chip that takes this one's place, else the one before it.
      const nextIndex = index < chips.length - 1 ? index : index - 1;
      removeChipAndFocus(index, nextIndex >= 0 ? nextIndex : 'input');
    };

    // The field looks like one text box, so a click on its empty part goes to
    // the input.
    const handleChipsMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
      if (disabled || event.target !== event.currentTarget) return;

      event.preventDefault();
      inputRef.current?.focus();
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
      onKeyDown?.(event);
      if (
        event.defaultPrevented ||
        disabled ||
        inputProps.readOnly ||
        event.nativeEvent.isComposing
      )
        return;

      if (event.key === 'Backspace' && inputValue === '' && chips.length > 0) {
        event.preventDefault();
        removeChipAndFocus(chips.length - 1, 'input');
        return;
      }

      // Tab stays plain navigation (WCAG 2.1.2 No Keyboard Trap). Committing on
      // Tab meant preventing it, which held focus for good whenever the consumer
      // rejected the value. handleBlur commits the pending text instead.
      if (event.key !== 'Enter' && event.key !== ',') return;

      event.preventDefault();
      addChips(event.key === ',' ? inputValue + event.key : inputValue);
    };

    const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
      if (addOnBlur && inputValue.trim()) {
        addChips(inputValue);
      }
      onBlur?.(event);
    };

    return (
      <>
        <div
          ref={fieldRef}
          className='eye:group/combobox-field eye:block eye:w-full'
        >
          <div
            className={cn(
              'eye:pb-6 eye:relative eye:flex eye:min-h-[44px] eye:w-full eye:items-start eye:rounded-md eye:border eye:bg-grey-5 eye:px-12 eye:py-8',
              'eye:focus-within:outline-2 eye:focus-within:outline-offset-3',
              disabled && 'eye:bg-grey-20',
              error
                ? 'eye:border-red-100 eye:hover:border-red-100'
                : 'hover:eye:border-black eye:border-grey-100',
              containerClassName,
            )}
          >
            {hasLabel ? (
              <Label
                htmlFor={inputId}
                className={cn(
                  'eye:body-s-highlight eye:absolute eye:top-0 eye:left-12 eye:z-10 eye:m-0 eye:max-w-[calc(100%-32px)] eye:-translate-y-1/2 eye:bg-label-gradient eye:px-[3px] eye:leading-none eye:whitespace-nowrap',
                  'eye:group-has-disabled/combobox-field:text-grey-disabled',
                  'eye:group-has-[[data-slot=chip-input-control][aria-invalid=true]]/combobox-field:text-red-100',
                )}
              >
                {label}
                {required ? <span aria-hidden='true'>*</span> : null}
              </Label>
            ) : null}
            <div
              role='group'
              aria-label={typeof label === 'string' ? label : undefined}
              className={cn(
                'eye:flex eye:w-full eye:flex-wrap eye:items-center eye:gap-4xs',
                chipsContainerClassName,
                chips.length > 0 && 'eye:mt-4xs',
              )}
              onMouseDown={handleChipsMouseDown}
            >
              {chips.map((chip, index) =>
                editable ? (
                  <EditableChip
                    key={chip.id}
                    value={chip.value}
                    disabled={disabled || inputProps.readOnly}
                    className={chipClassName}
                    labelClassName={chipLabelClassName}
                    onSave={(nextValue) => editChip(chip.id, nextValue)}
                    onRemove={() => handleRemoveChip(chip.id, index)}
                  />
                ) : (
                  <button
                    key={chip.id}
                    ref={(element) => {
                      chipRefs.current[index] = element;
                    }}
                    type='button'
                    disabled={disabled || inputProps.readOnly}
                    aria-label={`${t('delete')} ${chip.value}`}
                    className={cn(
                      comboboxChipVariants(),
                      'eye:leading-normal! eye:cursor-pointer eye:text-black eye:ring-inset eye:hover:bg-grey-40 eye:focus-visible:ring-2 eye:focus-visible:ring-(--stroke-color-black) eye:focus-visible:outline-none',
                      chipClassName,
                    )}
                    // The click moves focus to the input, so pressing must not
                    // focus the chip first.
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => removeChipAndFocus(index, 'input')}
                    onKeyDown={(event) => handleChipKeyDown(event, index)}
                  >
                    <span
                      className={cn('eye:text-grey-140', chipLabelClassName)}
                    >
                      {chip.value}
                    </span>
                    <X aria-hidden className='eye:size-12 eye:shrink-0' />
                  </button>
                ),
              )}

              {/* Browser suggestions and autocorrect would alter the entered
                  values. Callers can still opt back in. */}
              <input
                type='text'
                autoComplete='off'
                autoCorrect='off'
                autoCapitalize='none'
                spellCheck={false}
                {...inputProps}
                ref={inputRef}
                id={inputId}
                data-slot='chip-input-control'
                value={inputValue}
                disabled={disabled}
                required={required && chips.length === 0}
                placeholder={placeholder}
                aria-invalid={error}
                className={cn(
                  'eye:body-m eye:min-h-32 eye:min-w-[8rem] eye:flex-1 eye:self-center eye:bg-transparent eye:outline-none eye:placeholder:text-grey-placeholder!',
                  disabled && 'eye:cursor-not-allowed eye:text-grey-disabled',
                  inputClassName,
                )}
                onChange={(event) => setInputValue(event.target.value)}
                onKeyDown={handleKeyDown}
                onBlur={handleBlur}
              />
            </div>
          </div>
        </div>
        <span aria-live='polite' role='status' className='eye:sr-only'>
          {announcement}
        </span>
      </>
    );
  },
);

ChipInput.displayName = 'ChipInput';
